import type { ListColumn } from './contract.ts';

/**
 * Spaltentyp `signals` (AD-32, Story 7.23, Change 13.10.2026 Ampel-Symbole und Hilfe): eine Reihe kleiner Signale je
 * Zeile, z. B. die Ergebnisse der Auftragsampel je eingeschalteter Prüfung. Nur anzeigbar (kein Filter, keine Sortierung,
 * keine Massenbearbeitung). Rein – ohne Datenbank, Uhr und Browser; Zelle, `aria-label`, Tooltip, Export, MCP und die
 * Karte am Handy nutzen nur diese Funktionen (eine Quelle für Reihenfolge und Text).
 *
 * Regel (an genau dieser Stelle):
 * - Reihenfolge = Reihenfolge der `options` der Spalte (beim Besitzer festgelegt, z. B. `ORDER_CHECK_DEFINITIONS`);
 *   Name = `label` der Option. Unbekannte Schlüssel stehen am Ende (in gelieferter Reihenfolge), Name = Schlüssel.
 * - Zustand: `waiting` hat Vorrang vor der Farbe („wartet“), sonst das gelieferte Ergebnis. Keine eigene Ableitung.
 * - Grund: `reason`, im Wartezustand `reason – waitReason`; leer → Text ohne „ – …“.
 * - Text eines Signals: `<Name>: <grün|gelb|rot|wartet> – <Grund>`.
 * - Export/MCP: nur die nicht-grünen Signale, getrennt durch „; “; alles grün → leer.
 * - Handy: nur die nicht-grünen Signale.
 */

export const listSignalResults = ['green', 'yellow', 'red'] as const;
export type ListSignalResult = (typeof listSignalResults)[number];

/** Gespeicherter Wert eines Signals (z. B. `orders.order_list_row.checks`): nie Personen (`by`) oder Zusatzdaten. */
export interface ListSignal {
  id: string;
  result: ListSignalResult;
  waiting: boolean;
  reason: string | null;
  waitReason: string | null;
}

/** Angezeigter Zustand: Wartezustand vor der Farbe. */
export type ListSignalState = ListSignalResult | 'waiting';

export const listSignalStateLabels: Readonly<Record<ListSignalState, string>> = {
  green: 'grün',
  yellow: 'gelb',
  red: 'rot',
  waiting: 'wartet',
};

/** Ein Signal fertig zur Anzeige. */
export interface ResolvedListSignal {
  id: string;
  /** Name aus den Optionen der Spalte; unbekannt → Schlüssel. */
  label: string;
  state: ListSignalState;
  /** Grund (im Wartezustand mit Wartegrund) oder `null`. */
  reason: string | null;
  /** `<Name>: <Zustand> – <Grund>` (für `aria-label`, Export, MCP). */
  text: string;
  /** Schlüssel steht in den Optionen der Spalte. */
  known: boolean;
}

/** Trenner zwischen den Signalen im Export und im MCP-Lesen. */
export const LIST_SIGNALS_SEPARATOR = '; ';

const RESULTS: ReadonlySet<string> = new Set(listSignalResults);

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

/** Gespeicherten Wert lesen (JSON-Feld oder Text); Ungültiges fällt weg, nie ein Absturz. */
export function parseListSignals(value: unknown): ListSignal[] {
  let raw: unknown = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  const out: ListSignal[] = [];
  const seen = new Set<string>();
  for (const item of raw as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const r = item as Record<string, unknown>;
    if (typeof r.id !== 'string' || r.id === '' || seen.has(r.id)) continue;
    if (typeof r.result !== 'string' || !RESULTS.has(r.result)) continue;
    seen.add(r.id);
    out.push({ id: r.id, result: r.result as ListSignalResult, waiting: r.waiting === true, reason: text(r.reason), waitReason: text(r.waitReason) });
  }
  return out;
}

/** Zustand eines Signals: wartet vor der Farbe. */
export function listSignalState(signal: Pick<ListSignal, 'result' | 'waiting'>): ListSignalState {
  return signal.waiting ? 'waiting' : signal.result;
}

/** Grund eines Signals: im Wartezustand `reason – waitReason`. */
export function listSignalReason(signal: Pick<ListSignal, 'reason' | 'waitReason' | 'waiting'>): string | null {
  const parts = [signal.reason, signal.waiting ? signal.waitReason : null].filter((p): p is string => p !== null && p !== '');
  return parts.length > 0 ? parts.join(' – ') : null;
}

/** `<Name>: <Zustand> – <Grund>`; ohne Grund ohne „ – …“. */
export function listSignalText(label: string, state: ListSignalState, reason: string | null): string {
  return `${label}: ${listSignalStateLabels[state]}${reason ? ` – ${reason}` : ''}`;
}

/** Signale eines Werts in der Reihenfolge der Spalten-Optionen, mit Name, Zustand, Grund und Text. */
export function resolveListSignals(column: Pick<ListColumn, 'options'>, value: unknown): ResolvedListSignal[] {
  const options = column.options ?? [];
  const order = new Map(options.map((o, i) => [o.value, i]));
  const labels = new Map(options.map((o) => [o.value, o.label]));
  const signals = parseListSignals(value);
  const rank = (id: string) => order.get(id) ?? options.length;
  const sorted = signals.map((s, i) => ({ s, i })).sort((a, b) => rank(a.s.id) - rank(b.s.id) || a.i - b.i);
  return sorted.map(({ s }) => {
    const label = labels.get(s.id) ?? s.id;
    const state = listSignalState(s);
    const reason = listSignalReason(s);
    return { id: s.id, label, state, reason, text: listSignalText(label, state, reason), known: labels.has(s.id) };
  });
}

/** Nur die nicht-grünen Signale (Handy, Export, MCP). */
export function attentionListSignals(column: Pick<ListColumn, 'options'>, value: unknown): ResolvedListSignal[] {
  return resolveListSignals(column, value).filter((s) => s.state !== 'green');
}

/**
 * Text für Export und MCP: nicht-grüne Signale, getrennt durch „; “; alles grün → leer. Story 7.38: hat die Spalte Optionen,
 * zählen nur deren Schlüssel (die Auftragsampel nennt `status` nicht – Status zeigt die Pille).
 */
export function listSignalsText(column: Pick<ListColumn, 'options'>, value: unknown): string {
  const shownOnly = (column.options?.length ?? 0) > 0;
  return attentionListSignals(column, value)
    .filter((s) => !shownOnly || s.known)
    .map((s) => s.text)
    .join(LIST_SIGNALS_SEPARATOR);
}
